import { z } from 'zod';

import { api } from '@/lib/api';
import { useTenantQuery } from '@/lib/query/useTenantQuery';

const summarySchema = z.object({
  vehicles: z.number(),
  bookings: z.number(),
  clients: z.number(),
  summary: z.object({
    availableVehicles: z.number(),
    activeBookings: z.number(),
    pendingBookings: z.number().default(0),
    revenueMonth: z.number(),
    compliance: z.object({
      visite: z.number(),
      assurance: z.number(),
      vignette: z.number(),
      vidange: z.number(),
    }),
    clientRisk: z.object({
      blacklisted: z.number(),
      vip: z.number(),
    }),
  }),
});

export type DeskSummary = z.infer<typeof summarySchema>;

export function useDeskSummary() {
  return useTenantQuery(['desk-summary'], '/api/desk/limits', (payload) => {
    const parsed = summarySchema.safeParse(payload);
    if (!parsed.success) throw new Error('The desk totals could not be read.');
    return parsed.data;
  });
}

const optionMeta = z.object({ total: z.number(), shown: z.number() });

const vehicleOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string().default(''),
  model: z.string().default(''),
  licensePlate: z.string().default(''),
  status: z.string().default('available'),
  pricePerDay: z.number().default(0),
  location: z.string().default(''),
});

export type VehicleOption = z.infer<typeof vehicleOptionSchema>;

export function useVehicleOptions() {
  return useTenantQuery(['vehicle-options'], '/api/staff/vehicle-options', (payload) => {
    const parsed = z.object({ data: z.array(vehicleOptionSchema), meta: optionMeta }).safeParse(payload);
    if (!parsed.success) throw new Error('The vehicle list could not be read.');
    return parsed.data;
  });
}

const bookingOptionSchema = z.object({
  id: z.string(),
  customerName: z.string().default(''),
  vehicleName: z.string().default(''),
  vehicleId: z.string().default(''),
  status: z.string().default(''),
  pickupDate: z.string().default(''),
});

export type BookingOption = z.infer<typeof bookingOptionSchema>;

export function useBookingOptions() {
  return useTenantQuery(['booking-options'], '/api/booking-options', (payload) => {
    const parsed = z.object({ data: z.array(bookingOptionSchema), meta: optionMeta }).safeParse(payload);
    if (!parsed.success) throw new Error('The booking list could not be read.');
    return parsed.data;
  });
}

export async function saveLedgerRow(path: string, body: unknown, method: 'POST' | 'PATCH' | 'DELETE'): Promise<void> {
  await api(path, method === 'DELETE' ? { method } : { method, body: JSON.stringify(body) });
}
